/**
 * WeaveIn.ai Platform - Field Groups API Service
 *
 * Repeatable groups of com_fields. Admins define a group once with its child
 * fields, optionally mark it repeatable, and forms reference the group as a
 * single placeholder. Submitted data lives under field_data[group_name] as an
 * array of {child_field_name: value} objects.
 */

import axios, { AxiosInstance } from 'axios';
import { API_VERSION } from './organizations';
import { initializeActiveCboId } from '../../utils/auth';

import { config as productConfig } from '@/src/config';
import type { RecordTypeScope } from './recordTypes';
// ============================================================================
// TYPES
// ============================================================================

export interface FieldGroupChild {
  field_id: number;
  name: string;
  friendly_name: string;
  type: string;
  mandatory: boolean;
  group_order: number;
  /**
   * Set when type === 'field_group'. Points at a nested target group; the
   * RepeatableGroupRenderer recursively expands the target as a sub-repeatable
   * under each parent instance.
   */
  target_field_group_id?: number | null;
  /**
   * Dropdown options from com_fields.field_options — backend returns these
   * since the TRUS-77 fix (_load_children in field_groups.py). Two shapes
   * coexist in production: plain string[] and {label, value}[] — pass through
   * normalizeOptions() from fieldOptions.ts before rendering.
   */
  field_options?: Array<string | { label: string; value: string }> | null;
  /**
   * Conditional visibility rules from com_fields.visibility_rules. When set,
   * the child should only render when its conditions are satisfied.
   * Shape: { conditions: [...], logic: 'AND'|'OR', when_hidden: 'skip'|'clear' }
   */
  visibility_rules?: Record<string, any> | null;
  /**
   * Max length of a str child (com_fields.str_len). The renderer treats a str
   * field with str_len > 255 as long (multi-line) text — the same rule the
   * member profile's inline editor uses — so notes fields render as a textarea
   * rather than a single-line input (TRUS-579).
   */
  str_len?: number | null;
}

export interface FieldGroup {
  id: number;
  cbo_id: number;
  name: string;
  friendly_name: string;
  description?: string | null;
  is_repeatable: boolean;
  min_instances?: number | null;
  max_instances?: number | null;
  add_button_label?: string | null;
  /**
   * Admin-configured ordered list of com_fields.id to show as columns in the
   * compact row-summary on the member profile. When null/empty, the renderer
   * falls back to a heuristic (first text / select / date child).
   */
  summary_column_field_ids?: number[] | null;
  /**
   * com_fields row whose value gets set in bulk by row-level "Mark complete"
   * / "Defer" actions. When null, bulk status actions hide; only bulk Remove
   * is offered.
   */
  status_field_id?: number | null;
  /** Which records this group appears on (TRUS-402). */
  record_types?: RecordTypeScope[] | null;
  children: FieldGroupChild[];
}

export interface FieldGroupTemplate {
  id: number;
  cbo_id: number;
  field_group_id: number;
  name: string;
  description?: string | null;
  sort_order: number;
  /**
   * Key-value map of child field_name → preset value. Used to pre-fill a
   * newly-appended instance when the user picks the template from the
   * "+ Add" split-button dropdown.
   */
  values_json: Record<string, any>;
}

export interface FieldGroupCreate {
  name: string;
  friendly_name: string;
  description?: string | null;
  is_repeatable: boolean;
  min_instances?: number | null;
  max_instances?: number | null;
  add_button_label?: string | null;
  field_ids?: number[];
  /** TRUS-402: which records this group appears on. Placement is a property of
   *  the GROUP, not of the fields inside it — a group child's own
   *  `applies_to_record_types` does not move the group. Omit to default to
   *  members + contacts. */
  record_types?: RecordTypeScope[] | null;
}

export interface FieldGroupUpdate {
  friendly_name?: string;
  description?: string | null;
  is_repeatable?: boolean;
  min_instances?: number | null;
  max_instances?: number | null;
  add_button_label?: string | null;
  summary_column_field_ids?: number[] | null;
  status_field_id?: number | null;
  /** TRUS-402: see FieldGroupCreate.record_types. */
  record_types?: RecordTypeScope[] | null;
}

// ============================================================================
// API CLIENT
// ============================================================================

class FieldGroupsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    initializeActiveCboId();

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url;
        console.error('🧩 FieldGroups API Error:', {
          status,
          url,
          message: error.response?.data?.detail || error.message,
        });
        return Promise.reject(error);
      }
    );
  }

  /** TRUS-402: `record_type` narrows to groups that declare it, so a household
   *  surface lists household groups rather than every group in the org. */
  async list(params?: { record_type?: RecordTypeScope }): Promise<FieldGroup[]> {
    const response = await this.client.get<FieldGroup[]>('/api/field-groups', { params });
    return response.data;
  }

  /** TRUS-402: instances of every group placed on one household/family record.
   *  Returns {group_name: [instance values, ...]}. */
  async listRecordInstances(
    recordType: 'household' | 'family_member',
    ids: { householdId?: number; memberId?: number; familyMemberId?: number }
  ): Promise<Record<string, Record<string, any>[]>> {
    const url =
      recordType === 'household'
        ? `/api/households/${ids.householdId}/field-groups`
        : `/api/members/${ids.memberId}/family/${ids.familyMemberId}/field-groups`;
    const response = await this.client.get<Record<string, Record<string, any>[]>>(url);
    return response.data || {};
  }

  /** Replace every instance of ONE group on a household/family record. */
  async setRecordInstances(
    recordType: 'household' | 'family_member',
    ids: { householdId?: number; memberId?: number; familyMemberId?: number },
    groupName: string,
    instances: Record<string, any>[]
  ): Promise<{ group: string; instances: number }> {
    const url =
      recordType === 'household'
        ? `/api/households/${ids.householdId}/field-groups/${encodeURIComponent(groupName)}`
        : `/api/members/${ids.memberId}/family/${ids.familyMemberId}/field-groups/${encodeURIComponent(groupName)}`;
    const response = await this.client.put(url, { instances });
    return response.data;
  }

  async get(groupId: number): Promise<FieldGroup> {
    const response = await this.client.get<FieldGroup>(`/api/field-groups/${groupId}`);
    return response.data;
  }

  async create(data: FieldGroupCreate): Promise<FieldGroup> {
    const response = await this.client.post<FieldGroup>('/api/field-groups', data);
    return response.data;
  }

  async update(groupId: number, data: FieldGroupUpdate): Promise<FieldGroup> {
    const response = await this.client.put<FieldGroup>(`/api/field-groups/${groupId}`, data);
    return response.data;
  }

  async setChildren(groupId: number, fieldIds: number[]): Promise<FieldGroup> {
    const response = await this.client.put<FieldGroup>(
      `/api/field-groups/${groupId}/children`,
      { field_ids: fieldIds }
    );
    return response.data;
  }

  async delete(groupId: number): Promise<void> {
    await this.client.delete(`/api/field-groups/${groupId}`);
  }

  async listTemplates(groupId: number): Promise<FieldGroupTemplate[]> {
    const response = await this.client.get<FieldGroupTemplate[]>(
      `/api/field-groups/${groupId}/templates`
    );
    return response.data;
  }

  async createTemplate(
    groupId: number,
    data: Omit<FieldGroupTemplate, 'id' | 'cbo_id' | 'field_group_id'>
  ): Promise<FieldGroupTemplate> {
    const response = await this.client.post<FieldGroupTemplate>(
      `/api/field-groups/${groupId}/templates`,
      data
    );
    return response.data;
  }

  async deleteTemplate(groupId: number, templateId: number): Promise<void> {
    await this.client.delete(`/api/field-groups/${groupId}/templates/${templateId}`);
  }
}

export const fieldGroupsAPI = new FieldGroupsAPI();
